import React, { useState, useEffect } from 'react';
import { configAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { useSearchParams } from 'react-router-dom';

const Configuration = () => {
  const { user, isAdmin, changePassword } = useAuth();
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [addModalType, setAddModalType] = useState('');
  const [addModalValue, setAddModalValue] = useState('');
  const [addModalCategory, setAddModalCategory] = useState('positif');
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [configParams, setConfigParams] = useState({
    USL: 90,
    objectifTaux: 80,
    delaiSuivi: 90
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configResponse, usersResponse] = await Promise.all([
        configAPI.get(),
        isAdmin ? usersAPI.getAll() : Promise.resolve({ data: [] })
      ]);
      setConfig(configResponse.data);
      setConfigParams({
        USL: configResponse.data.USL || 90,
        objectifTaux: configResponse.data.objectifTaux || 80,
        delaiSuivi: configResponse.data.delaiSuivi || 90
      });
      setUsers(usersResponse.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (type, value, category = '') => {
    if (!window.confirm(`Supprimer "${value}" ?`)) return;

    try {
      await configAPI.removeItem(type, value, category);
      await fetchData();
      toast.success('Élément supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openAddModal = (type, category = '') => {
    setAddModalType(type);
    setAddModalCategory(category);
    setAddModalValue('');
    setShowAddModal(true);
  };

  const handleAddItem = async () => {
    if (!addModalValue.trim()) {
      toast.error('Valeur requise');
      return;
    }

    try {
      await configAPI.addItem(addModalType, addModalValue.trim(), addModalCategory);
      await fetchData();
      setShowAddModal(false);
      toast.success('Élément ajouté !');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await configAPI.update(configParams);
      toast.success('Configuration sauvegardée');
      await fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    const result = await changePassword(passwordForm.oldPassword, passwordForm.newPassword);
    if (result.success) {
      setShowPasswordModal(false);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const handleResetUserPassword = async (userId, username) => {
    const newPassword = prompt(`Nouveau mot de passe pour ${username}:`);
    if (!newPassword || newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await usersAPI.resetPassword(userId, newPassword);
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Supprimer l'utilisateur ${username} ?`)) return;

    try {
      await usersAPI.delete(userId);
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const getRoleLabel = (role) => {
    const roles = { admin: 'Admin', formateur: 'Formateur', lecture: 'Lecture' };
    return roles[role] || role;
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">⚙️ Configuration</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gestion des Listes */}
        <div className="section-card bg-white rounded-xl p-6">
          <h3 className="font-bold mb-4">📋 Gestion des Listes</h3>
          <div className="space-y-4">
            {isAdmin && (
              <>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="book" size={16} className="mr-1 text-gray-600" />
                      Filières
                    </h4>
                    <button
                      onClick={() => openAddModal('filieres')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.filieres?.map((f, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {f}
                        <button
                          onClick={() => handleDeleteItem('filieres', f)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="book" size={16} className="mr-1 text-gray-600" />
                      Types Formation
                    </h4>
                    <button
                      onClick={() => openAddModal('typesFormation')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.typesFormation?.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {t}
                        <button
                          onClick={() => handleDeleteItem('typesFormation', t)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="book" size={16} className="mr-1 text-gray-600" />
                      Diplômes
                    </h4>
                    <button
                      onClick={() => openAddModal('diplomes')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.diplomes?.map((d, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {d}
                        <button
                          onClick={() => handleDeleteItem('diplomes', d)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="book" size={16} className="mr-1 text-gray-600" />
                      Promotions
                    </h4>
                    <button
                      onClick={() => openAddModal('promotions')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.promotions?.map((p, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {p}
                        <button
                          onClick={() => handleDeleteItem('promotions', p)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="document" size={16} className="mr-1 text-gray-600" />
                      Statuts Entrée
                    </h4>
                    <button
                      onClick={() => openAddModal('statutsEntree')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.statutsEntree?.map((s, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {s}
                        <button
                          onClick={() => handleDeleteItem('statutsEntree', s)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="briefcase" size={16} className="mr-1 text-gray-600" />
                      Statuts Insertion
                    </h4>
                    <button
                      onClick={() => openAddModal('statutsInsertion')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto text-sm">
                    <div className="mb-2">
                      <strong className="text-green-700">✅ Positif:</strong>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {config?.statutsInsertion?.positif?.map((s, i) => (
                          <span key={i} className="flex items-center gap-1 bg-green-100 px-2 py-1 rounded">
                            {s}
                            <button
                              onClick={() => handleDeleteItem('statutsInsertion', s, 'positif')}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Icon name="close" size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <strong className="text-red-700 flex items-center">
                        <Icon name="error" size={16} className="mr-1" />
                        Négatif:
                      </strong>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {config?.statutsInsertion?.negatif?.map((s, i) => (
                          <span key={i} className="flex items-center gap-1 bg-red-100 px-2 py-1 rounded">
                            {s}
                            <button
                              onClick={() => handleDeleteItem('statutsInsertion', s, 'negatif')}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              aria-label={`Supprimer ${s}`}
                            >
                              <Icon name="close" size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="document" size={16} className="mr-1 text-gray-600" />
                      Types Contrat
                    </h4>
                    <button
                      onClick={() => openAddModal('typesContrat')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.typesContrat?.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {t}
                        <button
                          onClick={() => handleDeleteItem('typesContrat', t)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="money" size={16} className="mr-1 text-gray-600" />
                      Tranches Salariales
                    </h4>
                    <button
                      onClick={() => openAddModal('tranchesSalariales')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.tranchesSalariales?.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {t}
                        <button
                          onClick={() => handleDeleteItem('tranchesSalariales', t)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="check" size={16} className="mr-1 text-gray-600" />
                      Statuts Formation
                    </h4>
                    <button
                      onClick={() => openAddModal('statutsFormation')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.statutsFormation?.map((s, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {s}
                        <button
                          onClick={() => handleDeleteItem('statutsFormation', s)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="info" size={16} className="mr-1 text-gray-600" />
                      Situations Sortie
                    </h4>
                    <button
                      onClick={() => openAddModal('situationsSortie')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.situationsSortie?.map((s, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {s}
                        <button
                          onClick={() => handleDeleteItem('situationsSortie', s)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium flex items-center">
                      <Icon name="folder" size={16} className="mr-1 text-gray-600" />
                      États Dossier
                    </h4>
                    <button
                      onClick={() => openAddModal('etatsDossier')}
                      className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 max-h-28 overflow-y-auto text-sm flex flex-wrap gap-2">
                    {config?.etatsDossier?.map((s, i) => (
                      <span key={i} className="flex items-center gap-1 bg-white px-2 py-1 rounded">
                        {s}
                        <button
                          onClick={() => handleDeleteItem('etatsDossier', s)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Icon name="close" size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Paramètres et Utilisateurs */}
        <div className="space-y-6">
          {/* Utilisateur */}
          <div className="section-card bg-white rounded-xl p-6">
            <h3 className="font-bold mb-4 flex items-center text-gray-800">
              <Icon name="user" size={20} className="mr-2 text-red-600" />
              Utilisateur
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="font-semibold">{user?.name || '-'}</p>
              <p className="text-sm text-gray-500">{getRoleLabel(user?.role)}</p>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Icon name="lock" size={18} />
              <span>Changer mot de passe</span>
            </button>
          </div>

          {/* Paramètres SPC */}
          {isAdmin && (
            <div className="section-card bg-white rounded-xl p-6">
              <h3 className="font-bold mb-4 flex items-center text-gray-800">
                <Icon name="spc" size={20} className="mr-2 text-red-600" />
                Paramètres SPC
              </h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="config-usl" className="block text-sm font-medium mb-1">Délai max (jours)</label>
                  <input
                    id="config-usl"
                    name="USL"
                    type="number"
                    value={configParams.USL}
                    onChange={(e) => setConfigParams({ ...configParams, USL: parseInt(e.target.value) })}
                    onBlur={handleSaveConfig}
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <label htmlFor="config-objectif-taux" className="block text-sm font-medium mb-1">Objectif taux (%)</label>
                  <input
                    id="config-objectif-taux"
                    name="objectifTaux"
                    type="number"
                    value={configParams.objectifTaux}
                    onChange={(e) => setConfigParams({ ...configParams, objectifTaux: parseInt(e.target.value) })}
                    onBlur={handleSaveConfig}
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div>
                  <label htmlFor="config-delai-suivi" className="block text-sm font-medium mb-1">Délai suivi (jours)</label>
                  <input
                    id="config-delai-suivi"
                    name="delaiSuivi"
                    type="number"
                    value={configParams.delaiSuivi}
                    onChange={(e) => setConfigParams({ ...configParams, delaiSuivi: parseInt(e.target.value) })}
                    onBlur={handleSaveConfig}
                    autoComplete="off"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Gestion Utilisateurs (Admin) */}
          {isAdmin && (
            <div className="section-card bg-white rounded-xl p-6">
              <h3 className="font-bold mb-4 flex items-center text-gray-800">
                <Icon name="users" size={20} className="mr-2 text-red-600" />
                Utilisateurs
              </h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {users.map((u) => (
                  <div key={u._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{u.name || u.username}</p>
                      <p className="text-sm text-gray-500">{u.username} - {getRoleLabel(u.role)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleResetUserPassword(u._id, u.username)}
                        className="p-2 bg-amber-100 text-amber-700 rounded text-sm hover:bg-amber-200 transition-colors"
                        title="Réinitialiser mot de passe"
                        aria-label={`Réinitialiser le mot de passe de ${u.username}`}
                      >
                        <Icon name="lock" size={16} />
                      </button>
                      {u.username !== 'admin' && (
                        <button
                          onClick={() => handleDeleteUser(u._id, u.username)}
                          className="p-2 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200 transition-colors"
                          title="Supprimer"
                          aria-label={`Supprimer l'utilisateur ${u.username}`}
                        >
                          <Icon name="delete" size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Ajout */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={`Ajouter ${addModalType}`}
        size="sm"
      >
        {addModalType === 'statutsInsertion' && (
          <div className="mb-4">
            <label htmlFor="add-modal-category" className="block text-sm font-medium mb-1">Catégorie</label>
            <select
              id="add-modal-category"
              name="category"
              value={addModalCategory}
              onChange={(e) => setAddModalCategory(e.target.value)}
              autoComplete="off"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="positif">✅ Positif</option>
              <option value="negatif">❌ Négatif</option>
            </select>
          </div>
        )}
        <div className="mb-4">
          <label htmlFor="add-modal-value" className="block text-sm font-medium mb-1">Valeur</label>
          <input
            id="add-modal-value"
            name="value"
            type="text"
            value={addModalValue}
            onChange={(e) => setAddModalValue(e.target.value)}
            autoComplete="off"
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            placeholder="Nouvel élément..."
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAddModal(false)}
            className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Annuler
          </button>
          <button
            onClick={handleAddItem}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Ajouter
          </button>
        </div>
      </Modal>

      {/* Modal Mot de passe */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="🔐 Changer mot de passe"
        size="sm"
      >
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label htmlFor="password-old" className="block text-sm font-medium mb-1">Mot de passe actuel</label>
            <input
              id="password-old"
              name="oldPassword"
              type="password"
              value={passwordForm.oldPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
              autoComplete="current-password"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            />
          </div>
          <div>
            <label htmlFor="password-new" className="block text-sm font-medium mb-1">Nouveau mot de passe</label>
            <input
              id="password-new"
              name="newPassword"
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              autoComplete="new-password"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              minLength="6"
              required
            />
          </div>
          <div>
            <label htmlFor="password-confirm" className="block text-sm font-medium mb-1">Confirmer</label>
            <input
              id="password-confirm"
              name="confirmPassword"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              autoComplete="new-password"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowPasswordModal(false)}
              className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Changer
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Configuration;
